import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Plus, Minus, Trash2, ArrowRight } from 'lucide-react';
import { useGiftCart } from '../../contexts/GiftCartContext';
import { usePricing } from '../../contexts/PricingContext';
import { API_BASE_URL } from '../../config/apiConfig';

interface GiftCartModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCheckout: () => void;
}

const GiftCartModal: React.FC<GiftCartModalProps> = ({ isOpen, onClose, onCheckout }) => {
    const { cart, removeFromCart, addToCart, decrementQuantity, totalItems } = useGiftCart();
    const { displayCurrency, exchangeRates } = usePricing();

    const API_URL = API_BASE_URL.replace(/\/api$/, '');

    const convertGiftPrice = (price: number, baseCurrency: string = 'USD') => {
        if (baseCurrency === displayCurrency) return price;
        let usdAmount = price;
        if (baseCurrency !== 'USD') {
            const baseRate = exchangeRates[baseCurrency];
            if (baseRate) usdAmount = price / baseRate;
        }
        const targetRate = exchangeRates[displayCurrency] || 1;
        return usdAmount * targetRate;
    };

    const subtotal = cart.reduce((acc, item) => {
        const itemConvertedPrice = convertGiftPrice(Number(item.gift.price), item.gift.currency || 'USD');
        return acc + (itemConvertedPrice * item.quantity);
    }, 0);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                />

                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden border border-white/20 dark:border-slate-800"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="px-5 sm:px-8 py-4 sm:py-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between bg-gray-50/50 dark:bg-slate-800/50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                                <ShoppingBag className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Your Gift Box</h2>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{totalItems} items selected</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-slate-400"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Cart Items */}
                    <div className="px-5 sm:px-8 py-6 flex-1 overflow-y-auto custom-scrollbar">
                        {cart.length === 0 ? (
                            <div className="py-12 text-center">
                                <div className="w-20 h-20 bg-gray-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <ShoppingBag className="w-10 h-10 text-gray-300" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">Empty Box</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-[240px] mx-auto">Your gift box is empty. Explore our shop to find authentic savannah gear!</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {cart.map((item) => (
                                    <div key={item.gift.id} className="flex items-center gap-4 group">
                                        {/* Product Image */}
                                        <div className="w-20 h-20 rounded-2xl bg-gray-50 dark:bg-slate-800 flex-shrink-0 flex items-center justify-center p-2 border border-gray-100 dark:border-slate-700">
                                            <img
                                                src={item.gift.imageUrl && item.gift.imageUrl.startsWith('http') ? item.gift.imageUrl : `${API_URL}${item.gift.imageUrl && item.gift.imageUrl.startsWith('/') ? '' : '/'}${item.gift.imageUrl || ''}`}
                                                alt={item.gift.name}
                                                className="w-full h-full object-contain"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = 'https://placehold.co/100x100?text=Gift';
                                                }}
                                            />
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-slate-900 dark:text-white truncate">{item.gift.name}</h4>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-1">
                                                {displayCurrency === 'USD' ? '$' : displayCurrency} {convertGiftPrice(Number(item.gift.price), item.gift.currency || 'USD').toFixed(2)} / unit
                                            </p>
                                            
                                            {/* Quantity Controls */}
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-1 bg-gray-50 dark:bg-slate-800 rounded-lg p-1 border border-gray-100 dark:border-slate-700">
                                                    <button
                                                        onClick={() => decrementQuantity(item.gift.id)}
                                                        className="p-1 rounded-md hover:bg-white dark:hover:bg-slate-700 transition-colors text-slate-400"
                                                    >
                                                        <Minus size={12} />
                                                    </button>
                                                    <span className="w-6 text-center text-xs font-bold text-slate-700 dark:text-slate-200">{item.quantity}</span>
                                                    <button
                                                        onClick={() => addToCart(item.gift)}
                                                        className="p-1 rounded-md hover:bg-white dark:hover:bg-slate-700 transition-colors text-slate-400"
                                                    >
                                                        <Plus size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Total & Action */}
                                        <div className="text-right">
                                            <div className="font-black text-slate-900 dark:text-white mb-1">
                                                <span className="text-xs text-blue-600/60 mr-0.5">{displayCurrency === 'USD' ? '$' : displayCurrency}</span>
                                                {(convertGiftPrice(Number(item.gift.price), item.gift.currency || 'USD') * item.quantity).toFixed(2)}
                                            </div>
                                            <button
                                                onClick={() => removeFromCart(item.gift.id)}
                                                className="p-2 text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {cart.length > 0 && (
                        <div className="px-5 sm:px-8 py-6 bg-gray-50/50 dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-800">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <span className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Estimated Subtotal</span>
                                    <div className="text-xs text-slate-400 mt-1">Excludes shipping & taxes</div>
                                </div>
                                <div className="text-3xl font-black text-slate-900 dark:text-white">
                                    <span className="text-base text-blue-600/60 mr-1">{displayCurrency === 'USD' ? '$' : displayCurrency}</span>
                                    {subtotal.toFixed(2)}
                                </div>
                            </div>
                            
                            <button
                                onClick={onCheckout}
                                className="w-full bg-[#2A7ECE] hover:bg-[#1E63A8] text-white py-4 rounded-[1.5rem] font-bold shadow-xl shadow-blue-500/20 flex items-center justify-center gap-3 transition-all active:scale-95 group"
                            >
                                <span>Proceed to Secure Checkout</span>
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            
                            <p className="text-center text-[10px] text-slate-400 font-medium mt-4 flex items-center justify-center gap-1.5">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-emerald-500">
                                    <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                256-bit SSL Secure Encrypted Payment Rails
                            </p>
                        </div>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default GiftCartModal;
